import React, { useEffect, useMemo, useState } from 'react';
import { useQuery } from 'react-query';
import toast from 'react-hot-toast';
import {
  AlertCircle,
  CheckCircle2,
  Clock3,
  Download,
  Inbox,
  LifeBuoy,
  Loader2,
  MessageSquare,
  Paperclip,
  Plus,
  RefreshCw,
  Search,
  Send,
  ShieldCheck,
  SlidersHorizontal,
  UserCheck,
  X
} from 'lucide-react';
import PageHeader from '../../components/common/PageHeader';
import SkeletonBlock from '../../components/common/SkeletonBlock';
import { ADMIN_PERMISSIONS } from '../../constants';
import { adminAPI } from '../../services/api';
import useAuthStore from '../../store/authStore';
import { hasPermission } from '../../utils/rbac';

const statusOptions = [
  { value: 'active', label: 'Active' },
  { value: 'all', label: 'All' },
  { value: 'open', label: 'Open' },
  { value: 'in_progress', label: 'In progress' },
  { value: 'waiting_on_user', label: 'Waiting on user' },
  { value: 'resolved', label: 'Resolved' },
  { value: 'closed', label: 'Closed' }
];

const actionStatusOptions = statusOptions.filter((item) => !['active', 'all'].includes(item.value));
const priorityOptions = ['all', 'low', 'normal', 'high', 'urgent'];
const actionPriorityOptions = priorityOptions.filter((item) => item !== 'all');
const requesterOptions = ['all', 'candidate', 'employer', 'admin'];
const categoryOptions = ['support', 'bug', 'billing', 'account', 'kyc', 'matching', 'interview', 'offer', 'technical', 'other'];

const defaultFilters = {
  status: 'active',
  priority: 'all',
  requesterType: 'all',
  assignedTo: '',
  search: '',
  page: 1,
  limit: 25
};

const emptyCreateForm = {
  subject: '',
  description: '',
  category: 'bug',
  priority: 'normal',
  assignedAdminUserId: '',
  etaAt: ''
};

const extractError = (error, fallback) => (
  error?.response?.error ||
  error?.message ||
  fallback
);

const labelize = (value = '') => String(value || 'Not set')
  .replace(/[_-]+/g, ' ')
  .replace(/\b\w/g, (letter) => letter.toUpperCase())
  .replace(/\bKyc\b/g, 'KYC');

const formatDateTime = (value) => {
  if (!value) return 'Not set';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return 'Not set';
  return new Intl.DateTimeFormat('en-GB', {
    dateStyle: 'medium',
    timeStyle: 'short'
  }).format(date);
};

const formatDateInput = (value) => {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  const offset = date.getTimezoneOffset() * 60000;
  return new Date(date.getTime() - offset).toISOString().slice(0, 16);
};

const priorityTone = (priority) => {
  if (priority === 'urgent') return 'rose';
  if (priority === 'high') return 'amber';
  if (priority === 'low') return 'slate';
  return 'cyan';
};

const statusTone = (status) => {
  if (status === 'resolved') return 'emerald';
  if (status === 'closed') return 'slate';
  if (status === 'waiting_on_user') return 'amber';
  if (status === 'in_progress') return 'cyan';
  return 'blue';
};

const toneClasses = {
  blue: 'border-blue-200 bg-blue-50 text-blue-700',
  cyan: 'border-cyan-200 bg-cyan-50 text-cyan-700',
  emerald: 'border-emerald-200 bg-emerald-50 text-emerald-700',
  amber: 'border-amber-200 bg-amber-50 text-amber-700',
  rose: 'border-rose-200 bg-rose-50 text-rose-700',
  slate: 'border-slate-200 bg-slate-50 text-slate-600'
};

const Badge = ({ children, tone = 'slate' }) => (
  <span className={`inline-flex items-center rounded-md border px-2 py-1 text-xs font-semibold ${toneClasses[tone] || toneClasses.slate}`}>
    {children}
  </span>
);

const Field = ({ label, children }) => (
  <label className="block">
    <span className="mb-1.5 block text-xs font-semibold uppercase tracking-[0.14em] text-slate-500">{label}</span>
    {children}
  </label>
);

const inputClass = 'h-11 w-full rounded-lg border border-slate-200 bg-white px-3 text-sm text-slate-900 shadow-sm outline-none transition focus:border-slate-400 focus:ring-2 focus:ring-slate-100 disabled:cursor-not-allowed disabled:bg-slate-50 disabled:text-slate-400';
const textareaClass = 'min-h-[120px] w-full rounded-lg border border-slate-200 bg-white px-3 py-2.5 text-sm leading-6 text-slate-900 shadow-sm outline-none transition focus:border-slate-400 focus:ring-2 focus:ring-slate-100 disabled:cursor-not-allowed disabled:bg-slate-50 disabled:text-slate-400';

const StatCard = ({ icon: Icon, label, value, detail, tone = 'blue' }) => (
  <article className="rounded-lg border border-slate-100 bg-white p-5 shadow-sm">
    <div className="flex items-start justify-between gap-4">
      <div>
        <p className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-500">{label}</p>
        <p className="mt-2 text-2xl font-semibold tracking-tight text-slate-950">{Number(value || 0).toLocaleString()}</p>
      </div>
      <span className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-lg border ${toneClasses[tone] || toneClasses.blue}`}>
        <Icon size={18} />
      </span>
    </div>
    {detail && <p className="mt-3 text-xs leading-5 text-slate-500">{detail}</p>}
  </article>
);

const SupportTicketsSkeleton = () => (
  <div className="min-h-full px-4 pb-10 pt-20 sm:px-6 lg:px-8 lg:pt-8">
    <div className="mb-6 flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
      <div className="space-y-3">
        <SkeletonBlock className="h-4 w-36 rounded-md" />
        <SkeletonBlock className="h-10 w-80 rounded-lg" />
        <SkeletonBlock className="h-4 w-[42rem] max-w-full rounded-md" />
      </div>
      <SkeletonBlock className="h-10 w-36 rounded-lg" />
    </div>
    <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-5">
      {Array.from({ length: 5 }).map((_, index) => <SkeletonBlock key={index} className="h-28 rounded-lg" />)}
    </div>
    <div className="mt-5 grid gap-5 xl:grid-cols-[minmax(0,1fr)_460px]">
      <SkeletonBlock className="h-[640px] rounded-lg" />
      <SkeletonBlock className="h-[640px] rounded-lg" />
    </div>
  </div>
);

const EmptyState = ({ title, copy, icon: Icon = Inbox }) => (
  <div className="flex min-h-[220px] flex-col items-center justify-center rounded-lg border border-dashed border-slate-200 bg-slate-50/60 p-8 text-center">
    <span className="flex h-11 w-11 items-center justify-center rounded-lg border border-slate-200 bg-white text-slate-500">
      <Icon size={20} />
    </span>
    <h3 className="mt-4 text-sm font-semibold text-slate-950">{title}</h3>
    <p className="mt-2 max-w-sm text-sm leading-6 text-slate-500">{copy}</p>
  </div>
);

const fileLabel = (file) => {
  if (!file) return '';
  const size = file.size ? ` · ${(file.size / 1024 / 1024).toFixed(2)} MB` : '';
  return `${file.name}${size}`;
};

const appendIfPresent = (formData, key, value) => {
  if (value !== undefined && value !== null && value !== '') formData.append(key, value);
};

const buildTicketFormData = (form, file) => {
  const formData = new FormData();
  appendIfPresent(formData, 'subject', form.subject);
  appendIfPresent(formData, 'description', form.description);
  appendIfPresent(formData, 'category', form.category);
  appendIfPresent(formData, 'priority', form.priority);
  appendIfPresent(formData, 'assignedAdminUserId', form.assignedAdminUserId);
  appendIfPresent(formData, 'etaAt', form.etaAt ? new Date(form.etaAt).toISOString() : '');
  if (file) formData.append('attachment', file);
  return formData;
};

const CreateTicketModal = ({ assignees, onClose, onCreated }) => {
  const [form, setForm] = useState(emptyCreateForm);
  const [file, setFile] = useState(null);
  const [saving, setSaving] = useState(false);

  const update = (field, value) => setForm((current) => ({ ...current, [field]: value }));

  const submit = async (event) => {
    event.preventDefault();
    setSaving(true);
    try {
      await adminAPI.createSupportTicket(buildTicketFormData(form, file));
      toast.success('Ticket created.');
      onCreated();
      onClose();
    } catch (error) {
      toast.error(extractError(error, 'Ticket could not be created.'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-slate-950/50 px-4 py-8">
      <div className="w-full max-w-3xl overflow-hidden rounded-lg bg-white shadow-xl">
        <div className="flex items-start justify-between gap-4 border-b border-slate-100 px-6 py-5">
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-500">Admin ticket</p>
            <h2 className="mt-1 text-lg font-semibold text-slate-950">Create support ticket</h2>
          </div>
          <button type="button" onClick={onClose} className="flex h-9 w-9 items-center justify-center rounded-lg text-slate-400 transition hover:bg-slate-100 hover:text-slate-700">
            <X size={18} />
          </button>
        </div>
        <form onSubmit={submit} className="space-y-5 px-6 py-5">
          <Field label="Subject">
            <input className={inputClass} value={form.subject} onChange={(event) => update('subject', event.target.value)} required maxLength={220} placeholder="Briefly describe the issue" />
          </Field>
          <div className="grid gap-4 md:grid-cols-3">
            <Field label="Category">
              <select className={inputClass} value={form.category} onChange={(event) => update('category', event.target.value)}>
                {categoryOptions.map((item) => <option key={item} value={item}>{labelize(item)}</option>)}
              </select>
            </Field>
            <Field label="Priority">
              <select className={inputClass} value={form.priority} onChange={(event) => update('priority', event.target.value)}>
                {actionPriorityOptions.map((item) => <option key={item} value={item}>{labelize(item)}</option>)}
              </select>
            </Field>
            <Field label="ETA">
              <input className={inputClass} type="datetime-local" value={form.etaAt} onChange={(event) => update('etaAt', event.target.value)} />
            </Field>
          </div>
          <Field label="Assign to">
            <select className={inputClass} value={form.assignedAdminUserId} onChange={(event) => update('assignedAdminUserId', event.target.value)}>
              <option value="">Auto assign to support</option>
              {assignees.map((admin) => (
                <option key={admin.userId} value={admin.userId}>{admin.name} · {admin.openTicketCount} open</option>
              ))}
            </select>
          </Field>
          <Field label="Description">
            <textarea className={textareaClass} value={form.description} onChange={(event) => update('description', event.target.value)} required placeholder="Add the context, expected behavior, actual behavior, and any affected account or page." />
          </Field>
          <div className="rounded-lg border border-dashed border-slate-200 bg-slate-50 px-4 py-4">
            <label className="flex cursor-pointer items-center justify-between gap-4">
              <span className="min-w-0">
                <span className="flex items-center gap-2 text-sm font-semibold text-slate-900"><Paperclip size={16} /> Attachment</span>
                <span className="mt-1 block truncate text-xs text-slate-500">{file ? fileLabel(file) : 'PNG, JPG, or PDF up to 8 MB'}</span>
              </span>
              <span className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-xs font-semibold text-slate-700">Choose file</span>
              <input type="file" accept=".png,.jpg,.jpeg,.pdf" className="sr-only" onChange={(event) => setFile(event.target.files?.[0] || null)} />
            </label>
          </div>
          <div className="flex flex-col-reverse gap-3 border-t border-slate-100 pt-5 sm:flex-row sm:justify-end">
            <button type="button" onClick={onClose} className="inline-flex h-10 items-center justify-center rounded-lg border border-slate-200 px-4 text-sm font-semibold text-slate-700 hover:bg-slate-50">Cancel</button>
            <button type="submit" disabled={saving} className="inline-flex h-10 items-center justify-center gap-2 rounded-lg bg-slate-950 px-4 text-sm font-semibold text-white hover:bg-slate-800 disabled:cursor-not-allowed disabled:opacity-60">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus size={16} />} Create ticket
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const TicketCard = ({ ticket, selected, onSelect }) => (
  <button
    type="button"
    onClick={() => onSelect(ticket)}
    className={`w-full rounded-lg border bg-white p-4 text-left shadow-sm transition hover:border-slate-300 ${selected ? 'border-slate-900 ring-2 ring-slate-100' : 'border-slate-100'}`}
  >
    <div className="flex items-start justify-between gap-3">
      <div className="min-w-0">
        <p className="text-xs font-semibold uppercase tracking-[0.14em] text-slate-500">{ticket.ticketNumber}</p>
        <h3 className="mt-1 line-clamp-2 text-sm font-semibold leading-5 text-slate-950">{ticket.subject}</h3>
      </div>
      <Badge tone={priorityTone(ticket.priority)}>{labelize(ticket.priority)}</Badge>
    </div>
    <div className="mt-3 flex flex-wrap items-center gap-2">
      <Badge tone={statusTone(ticket.status)}>{labelize(ticket.status)}</Badge>
      <Badge>{labelize(ticket.requesterType)}</Badge>
      <Badge>{labelize(ticket.category)}</Badge>
    </div>
    <div className="mt-4 grid gap-2 text-xs text-slate-500 sm:grid-cols-2">
      <span className="truncate">Requester: <strong className="font-semibold text-slate-700">{ticket.requester?.name || ticket.requester?.email || 'Unknown'}</strong></span>
      <span className="truncate">Assignee: <strong className="font-semibold text-slate-700">{ticket.assignee?.name || 'Unassigned'}</strong></span>
      <span className="truncate">Updated: {formatDateTime(ticket.updatedAt)}</span>
      <span className="truncate">ETA: {formatDateTime(ticket.etaAt)}</span>
    </div>
  </button>
);

const Timeline = ({ updates = [], attachments = [], onOpenAttachment, openingAttachment }) => {
  const attachmentByUpdate = attachments.reduce((groups, attachment) => {
    const key = attachment.updateId || 'none';
    groups[key] = [...(groups[key] || []), attachment];
    return groups;
  }, {});

  return (
    <div className="space-y-4">
      {updates.map((update) => {
        const updateAttachments = attachmentByUpdate[update.id] || [];
        return (
          <article key={update.id} className="rounded-lg border border-slate-100 bg-white p-4 shadow-sm">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div>
                <p className="text-sm font-semibold text-slate-950">{update.author?.name || labelize(update.authorType)}</p>
                <p className="mt-0.5 text-xs text-slate-500">{formatDateTime(update.createdAt)} · {labelize(update.updateType)}</p>
              </div>
              {update.isInternal && <Badge tone="amber">Internal</Badge>}
            </div>
            <p className="mt-3 whitespace-pre-line text-sm leading-6 text-slate-600">{update.message}</p>
            {updateAttachments.length > 0 && (
              <div className="mt-3 flex flex-wrap gap-2">
                {updateAttachments.map((attachment) => (
                  <button
                    key={attachment.id}
                    type="button"
                    onClick={() => onOpenAttachment(attachment)}
                    disabled={openingAttachment === attachment.id}
                    className="inline-flex h-9 items-center gap-2 rounded-lg border border-slate-200 bg-slate-50 px-3 text-xs font-semibold text-slate-700 hover:bg-white disabled:opacity-60"
                  >
                    {openingAttachment === attachment.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Download size={14} />}
                    {attachment.originalFilename}
                  </button>
                ))}
              </div>
            )}
          </article>
        );
      })}
    </div>
  );
};

const TicketDetail = ({ ticketId, assignees, canManage, onChanged }) => {
  const [actionForm, setActionForm] = useState(null);
  const [messageForm, setMessageForm] = useState({ message: '', isInternal: false });
  const [messageFile, setMessageFile] = useState(null);
  const [savingAction, setSavingAction] = useState(false);
  const [savingMessage, setSavingMessage] = useState(false);
  const [openingAttachment, setOpeningAttachment] = useState(null);

  const detailQuery = useQuery(
    ['admin-support-ticket', ticketId],
    () => adminAPI.getSupportTicket(ticketId),
    { enabled: Boolean(ticketId), keepPreviousData: true }
  );

  const ticket = detailQuery.data?.ticket;
  const updates = detailQuery.data?.updates || [];
  const attachments = detailQuery.data?.attachments || [];

  useEffect(() => {
    if (!ticket) return;
    setActionForm({
      status: ticket.status || 'open',
      priority: ticket.priority || 'normal',
      category: ticket.category || 'support',
      assignedAdminUserId: ticket.assignedAdminUserId || '',
      etaAt: formatDateInput(ticket.etaAt),
      resolutionSummary: ticket.resolutionSummary || '',
      message: '',
      isInternal: false
    });
  }, [ticket]);

  const saveAction = async (event) => {
    event.preventDefault();
    if (!ticket || !actionForm) return;
    setSavingAction(true);
    try {
      await adminAPI.updateSupportTicket(ticket.id, {
        ...actionForm,
        assignedAdminUserId: actionForm.assignedAdminUserId || null,
        etaAt: actionForm.etaAt ? new Date(actionForm.etaAt).toISOString() : null
      });
      toast.success('Ticket updated.');
      await detailQuery.refetch();
      onChanged();
    } catch (error) {
      toast.error(extractError(error, 'Ticket update failed.'));
    } finally {
      setSavingAction(false);
    }
  };

  const addUpdate = async (event) => {
    event.preventDefault();
    if (!ticket) return;
    setSavingMessage(true);
    try {
      const formData = new FormData();
      appendIfPresent(formData, 'message', messageForm.message);
      appendIfPresent(formData, 'isInternal', messageForm.isInternal ? 'true' : 'false');
      if (messageFile) formData.append('attachment', messageFile);
      await adminAPI.addSupportTicketUpdate(ticket.id, formData);
      toast.success('Timeline updated.');
      setMessageForm({ message: '', isInternal: false });
      setMessageFile(null);
      await detailQuery.refetch();
      onChanged();
    } catch (error) {
      toast.error(extractError(error, 'Timeline update failed.'));
    } finally {
      setSavingMessage(false);
    }
  };

  const openAttachment = async (attachment) => {
    setOpeningAttachment(attachment.id);
    try {
      const blob = await adminAPI.downloadSupportTicketAttachment(attachment.ticketId, attachment.id);
      const url = URL.createObjectURL(blob);
      window.open(url, '_blank', 'noopener,noreferrer');
      setTimeout(() => URL.revokeObjectURL(url), 30000);
    } catch (error) {
      toast.error(extractError(error, 'Attachment could not be opened.'));
    } finally {
      setOpeningAttachment(null);
    }
  };

  if (!ticketId) {
    return <EmptyState title="Select a ticket" copy="Open a ticket from the queue to review details, timeline, assignment, ETA, and attachments." icon={LifeBuoy} />;
  }

  if (detailQuery.isLoading || !ticket || !actionForm) {
    return (
      <div className="space-y-4">
        <SkeletonBlock className="h-32 rounded-lg" />
        <SkeletonBlock className="h-72 rounded-lg" />
        <SkeletonBlock className="h-80 rounded-lg" />
      </div>
    );
  }

  if (detailQuery.isError) {
    return <EmptyState title="Ticket unavailable" copy={extractError(detailQuery.error, 'Ticket details could not be loaded.')} icon={AlertCircle} />;
  }

  return (
    <div className="space-y-5">
      <section className="rounded-lg border border-slate-100 bg-white p-5 shadow-sm">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0">
            <p className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-500">{ticket.ticketNumber}</p>
            <h2 className="mt-1 text-lg font-semibold leading-6 text-slate-950">{ticket.subject}</h2>
            <p className="mt-2 whitespace-pre-line text-sm leading-6 text-slate-600">{ticket.description}</p>
          </div>
          <div className="flex flex-wrap gap-2">
            <Badge tone={statusTone(ticket.status)}>{labelize(ticket.status)}</Badge>
            <Badge tone={priorityTone(ticket.priority)}>{labelize(ticket.priority)}</Badge>
          </div>
        </div>
        <div className="mt-5 grid gap-3 text-sm sm:grid-cols-2">
          <div className="rounded-lg bg-slate-50 p-3">
            <p className="text-xs font-semibold uppercase tracking-[0.14em] text-slate-500">Requester</p>
            <p className="mt-1 truncate font-semibold text-slate-900">{ticket.requester?.name || ticket.requester?.email || 'Unknown'}</p>
            <p className="mt-1 truncate text-xs text-slate-500">{ticket.requester?.email || labelize(ticket.requesterType)}</p>
          </div>
          <div className="rounded-lg bg-slate-50 p-3">
            <p className="text-xs font-semibold uppercase tracking-[0.14em] text-slate-500">Assignment</p>
            <p className="mt-1 truncate font-semibold text-slate-900">{ticket.assignee?.name || 'Unassigned'}</p>
            <p className="mt-1 truncate text-xs text-slate-500">ETA: {formatDateTime(ticket.etaAt)}</p>
          </div>
        </div>
      </section>

      <section className="rounded-lg border border-slate-100 bg-white p-5 shadow-sm">
        <div className="mb-4 flex items-center justify-between gap-3">
          <div>
            <h3 className="text-sm font-semibold text-slate-950">Action ticket</h3>
            <p className="mt-1 text-xs text-slate-500">Update ownership, priority, status, ETA, and resolution notes.</p>
          </div>
          {!canManage && <Badge tone="amber">View only</Badge>}
        </div>
        <form onSubmit={saveAction} className="space-y-4">
          <div className="grid gap-3 sm:grid-cols-2">
            <Field label="Status">
              <select className={inputClass} value={actionForm.status} disabled={!canManage} onChange={(event) => setActionForm((current) => ({ ...current, status: event.target.value }))}>
                {actionStatusOptions.map((item) => <option key={item.value} value={item.value}>{item.label}</option>)}
              </select>
            </Field>
            <Field label="Priority">
              <select className={inputClass} value={actionForm.priority} disabled={!canManage} onChange={(event) => setActionForm((current) => ({ ...current, priority: event.target.value }))}>
                {actionPriorityOptions.map((item) => <option key={item} value={item}>{labelize(item)}</option>)}
              </select>
            </Field>
          </div>
          <Field label="Assign to">
            <select className={inputClass} value={actionForm.assignedAdminUserId} disabled={!canManage} onChange={(event) => setActionForm((current) => ({ ...current, assignedAdminUserId: event.target.value }))}>
              <option value="">Unassigned</option>
              {assignees.map((admin) => (
                <option key={admin.userId} value={admin.userId}>{admin.name} · {admin.openTicketCount} open</option>
              ))}
            </select>
          </Field>
          <div className="grid gap-3 sm:grid-cols-2">
            <Field label="Category">
              <select className={inputClass} value={actionForm.category} disabled={!canManage} onChange={(event) => setActionForm((current) => ({ ...current, category: event.target.value }))}>
                {categoryOptions.map((item) => <option key={item} value={item}>{labelize(item)}</option>)}
              </select>
            </Field>
            <Field label="ETA">
              <input className={inputClass} type="datetime-local" value={actionForm.etaAt} disabled={!canManage} onChange={(event) => setActionForm((current) => ({ ...current, etaAt: event.target.value }))} />
            </Field>
          </div>
          <Field label="Resolution summary">
            <textarea className="min-h-[86px] w-full rounded-lg border border-slate-200 px-3 py-2.5 text-sm leading-6 outline-none focus:border-slate-400 focus:ring-2 focus:ring-slate-100 disabled:bg-slate-50" value={actionForm.resolutionSummary} disabled={!canManage} onChange={(event) => setActionForm((current) => ({ ...current, resolutionSummary: event.target.value }))} placeholder="Required context when resolving the ticket." />
          </Field>
          <Field label="Action note">
            <textarea className="min-h-[86px] w-full rounded-lg border border-slate-200 px-3 py-2.5 text-sm leading-6 outline-none focus:border-slate-400 focus:ring-2 focus:ring-slate-100 disabled:bg-slate-50" value={actionForm.message} disabled={!canManage} onChange={(event) => setActionForm((current) => ({ ...current, message: event.target.value }))} placeholder="Optional timeline note for this action." />
          </Field>
          <label className="flex items-center gap-2 text-sm font-medium text-slate-600">
            <input type="checkbox" checked={actionForm.isInternal} disabled={!canManage} onChange={(event) => setActionForm((current) => ({ ...current, isInternal: event.target.checked }))} />
            Internal note
          </label>
          <button type="submit" disabled={!canManage || savingAction} className="inline-flex h-10 w-full items-center justify-center gap-2 rounded-lg bg-slate-950 px-4 text-sm font-semibold text-white hover:bg-slate-800 disabled:cursor-not-allowed disabled:opacity-60">
            {savingAction ? <Loader2 className="h-4 w-4 animate-spin" /> : <ShieldCheck size={16} />} Save action
          </button>
        </form>
      </section>

      <section className="rounded-lg border border-slate-100 bg-white p-5 shadow-sm">
        <h3 className="text-sm font-semibold text-slate-950">Add update</h3>
        <form onSubmit={addUpdate} className="mt-4 space-y-4">
          <textarea className={textareaClass} value={messageForm.message} disabled={!canManage} onChange={(event) => setMessageForm((current) => ({ ...current, message: event.target.value }))} placeholder="Write a public reply or internal note." />
          <div className="rounded-lg border border-dashed border-slate-200 bg-slate-50 px-4 py-3">
            <label className="flex cursor-pointer items-center justify-between gap-4">
              <span className="min-w-0 truncate text-sm text-slate-600">{messageFile ? fileLabel(messageFile) : 'Attach screenshot or PDF'}</span>
              <span className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-xs font-semibold text-slate-700">Choose</span>
              <input type="file" accept=".png,.jpg,.jpeg,.pdf" className="sr-only" disabled={!canManage} onChange={(event) => setMessageFile(event.target.files?.[0] || null)} />
            </label>
          </div>
          <label className="flex items-center gap-2 text-sm font-medium text-slate-600">
            <input type="checkbox" checked={messageForm.isInternal} disabled={!canManage} onChange={(event) => setMessageForm((current) => ({ ...current, isInternal: event.target.checked }))} />
            Internal note
          </label>
          <button type="submit" disabled={!canManage || savingMessage || (!messageForm.message.trim() && !messageFile)} className="inline-flex h-10 w-full items-center justify-center gap-2 rounded-lg border border-slate-900 bg-white px-4 text-sm font-semibold text-slate-950 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-60">
            {savingMessage ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send size={16} />} Add update
          </button>
        </form>
      </section>

      <section>
        <div className="mb-3 flex items-center justify-between">
          <h3 className="text-sm font-semibold text-slate-950">Timeline</h3>
          <Badge>{updates.length} updates</Badge>
        </div>
        {updates.length ? (
          <Timeline updates={updates} attachments={attachments} onOpenAttachment={openAttachment} openingAttachment={openingAttachment} />
        ) : (
          <EmptyState title="No timeline yet" copy="Ticket updates will appear here after a support action or requester reply." icon={MessageSquare} />
        )}
      </section>
    </div>
  );
};

function SupportTicketsPage() {
  const { user } = useAuthStore();
  const canManage = hasPermission(user, ADMIN_PERMISSIONS.TICKETS_MANAGE);
  const [filters, setFilters] = useState(defaultFilters);
  const [selectedTicketId, setSelectedTicketId] = useState(null);
  const [showCreate, setShowCreate] = useState(false);

  const ticketsQuery = useQuery(
    ['admin-support-tickets', filters],
    () => adminAPI.getSupportTickets(filters),
    { keepPreviousData: true }
  );
  const assigneesQuery = useQuery('admin-support-ticket-assignees', adminAPI.getSupportTicketAssignees);

  const tickets = useMemo(() => ticketsQuery.data?.tickets || [], [ticketsQuery.data?.tickets]);
  const stats = ticketsQuery.data?.stats || {};
  const assignees = assigneesQuery.data?.assignees || [];

  useEffect(() => {
    if (!selectedTicketId && tickets.length) setSelectedTicketId(tickets[0].id);
  }, [selectedTicketId, tickets]);

  const updateFilter = (field, value) => setFilters((current) => ({
    ...current,
    [field]: value,
    page: 1
  }));

  const refresh = () => ticketsQuery.refetch();

  if (ticketsQuery.isLoading && !tickets.length) return <SupportTicketsSkeleton />;

  return (
    <div className="min-h-full px-4 pb-10 pt-20 sm:px-6 lg:px-8 lg:pt-8">
      <PageHeader
        eyebrow="Support"
        title="Ticket workspace"
        description="Triage customer issues, assign ownership, set ETA, maintain a timeline, and resolve tickets from one support queue."
        actions={(
          <>
            <button type="button" onClick={refresh} className="inline-flex h-10 items-center gap-2 rounded-lg border border-slate-200 bg-white px-4 text-sm font-semibold text-slate-700 shadow-sm hover:bg-slate-50">
              <RefreshCw size={16} className={ticketsQuery.isFetching ? 'animate-spin' : ''} /> Refresh
            </button>
            {canManage && (
              <button type="button" onClick={() => setShowCreate(true)} className="inline-flex h-10 items-center gap-2 rounded-lg bg-slate-950 px-4 text-sm font-semibold text-white shadow-sm hover:bg-slate-800">
                <Plus size={16} /> New ticket
              </button>
            )}
          </>
        )}
      />

      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-5">
        <StatCard icon={LifeBuoy} label="Active" value={stats.active} detail="Open or in progress" tone="blue" />
        <StatCard icon={AlertCircle} label="Urgent" value={stats.urgent} detail="Needs immediate review" tone="rose" />
        <StatCard icon={UserCheck} label="Unassigned" value={stats.unassigned} detail="No owner yet" tone="amber" />
        <StatCard icon={Clock3} label="Waiting" value={stats.waitingOnUser} detail="Awaiting requester reply" tone="cyan" />
        <StatCard icon={CheckCircle2} label="Resolved" value={stats.resolved} detail="Completed tickets" tone="emerald" />
      </div>

      {ticketsQuery.isError && (
        <div className="mt-5 rounded-lg border border-rose-100 bg-rose-50 px-4 py-3 text-sm text-rose-700">
          {extractError(ticketsQuery.error, 'Support tickets could not be loaded.')}
        </div>
      )}

      <section className="mt-5 rounded-lg border border-slate-100 bg-white p-4 shadow-sm">
        <div className="grid gap-3 lg:grid-cols-[minmax(0,1fr)_160px_160px_170px_180px]">
          <label className="relative block">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
            <input className={`${inputClass} pl-9`} value={filters.search} onChange={(event) => updateFilter('search', event.target.value)} placeholder="Search ticket, requester, subject..." />
          </label>
          <select className={inputClass} value={filters.status} onChange={(event) => updateFilter('status', event.target.value)}>
            {statusOptions.map((item) => <option key={item.value} value={item.value}>{item.label}</option>)}
          </select>
          <select className={inputClass} value={filters.priority} onChange={(event) => updateFilter('priority', event.target.value)}>
            {priorityOptions.map((item) => <option key={item} value={item}>{labelize(item)}</option>)}
          </select>
          <select className={inputClass} value={filters.requesterType} onChange={(event) => updateFilter('requesterType', event.target.value)}>
            {requesterOptions.map((item) => <option key={item} value={item}>{labelize(item)}</option>)}
          </select>
          <select className={inputClass} value={filters.assignedTo} onChange={(event) => updateFilter('assignedTo', event.target.value)}>
            <option value="">Any assignee</option>
            <option value="me">Assigned to me</option>
            <option value="unassigned">Unassigned</option>
            {assignees.map((admin) => <option key={admin.userId} value={admin.userId}>{admin.name}</option>)}
          </select>
        </div>
      </section>

      <div className="mt-5 grid gap-5 xl:grid-cols-[minmax(0,1fr)_460px]">
        <section className="space-y-3">
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-2 text-sm font-semibold text-slate-950">
              <SlidersHorizontal size={16} /> Queue
            </div>
            {ticketsQuery.isFetching && <span className="text-xs font-medium text-slate-500">Updating...</span>}
          </div>
          {tickets.length ? tickets.map((ticket) => (
            <TicketCard key={ticket.id} ticket={ticket} selected={ticket.id === selectedTicketId} onSelect={(next) => setSelectedTicketId(next.id)} />
          )) : (
            <EmptyState title="No tickets match" copy="Adjust the filters or create a new support ticket from this workspace." icon={Inbox} />
          )}
        </section>

        <aside className="xl:sticky xl:top-6 xl:self-start">
          <TicketDetail ticketId={selectedTicketId} assignees={assignees} canManage={canManage} onChanged={refresh} />
        </aside>
      </div>

      {showCreate && (
        <CreateTicketModal
          assignees={assignees}
          onClose={() => setShowCreate(false)}
          onCreated={refresh}
        />
      )}
    </div>
  );
}

export default SupportTicketsPage;
